import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from '@tanstack/react-table';

import type { PaginatedThreadsResponseDataItem } from '@/api/generated.schemas';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useOpen } from '@/hooks/use-open';
import FeedbackMetrics from '@/routes/(app)/agents/components/feedback-metrics';
import FeedbackPanel from '@/routes/(app)/agents/components/feedback-panel';
import ThreadsTable from '@/routes/(app)/agents/components/threads-table';
import { TracingPanel } from '@/routes/(app)/agents/components/tracing-panel';

const FeedbackAgent = () => {
  const { t } = useTranslation();
  const {
    open: openTracing,
    onOpenChangeWithValue: onOpenTracingWithValue,
    value: tracingValue,
  } = useOpen<string>({
    defaultOpen: false,
  });
  const {
    open: openFeedback,
    onOpenChangeWithValue: onOpenFeedbackWithValue,
    value: feedbackValue,
  } = useOpen<string>({
    defaultOpen: false,
  });

  const handleThreadTracing = useCallback(
    (row: Row<PaginatedThreadsResponseDataItem>) => {
      onOpenTracingWithValue(row.original.id);
    },
    [onOpenTracingWithValue]
  );

  const handleThreadFeedback = useCallback(
    (row: Row<PaginatedThreadsResponseDataItem>) => {
      onOpenFeedbackWithValue(row.original.id);
    },
    [onOpenFeedbackWithValue]
  );

  return (
    <ResizablePanelGroup direction='horizontal'>
      <ResizablePanel className='m-2 flex flex-col gap-4'>
        <h1 className='text-4xl font-bold'>{t('feedbackAgent.title')}</h1>
        <p className='text-muted-foreground'>
          {t('feedbackAgent.description')}
        </p>
        <FeedbackMetrics />
        <ThreadsTable
          onThreadTracing={handleThreadTracing}
          onThreadFeedback={handleThreadFeedback}
        />
      </ResizablePanel>
      {openTracing && tracingValue && (
        <>
          <ResizableHandle withHandle />
          <ResizablePanel className='m-2'>
            <TracingPanel
              threadId={tracingValue}
              componentName={'chat-agent'}
            />
          </ResizablePanel>
        </>
      )}
      {openFeedback && feedbackValue && (
        <>
          <ResizableHandle withHandle />
          <ResizablePanel className='m-2'>
            <FeedbackPanel threadId={feedbackValue} />
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
};

export default FeedbackAgent;
